import useCommon from "@/shared/hooks/useCommon";
import { useAddCartItem } from "@/shared/hooks/useCartHook";
import { ProductStatusEnums } from "@/shared/utils/enum";
import {
  isGreaterThanZero,
  formatPrice,
  withCloudinaryBackgroundRemoval,
} from "@/shared/utils/helpers";
import { Product } from "@/shared/utils/interfaces";
import { motion } from "framer-motion";
import { useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import EmptyImage from "../../../App/Assets/Images/empty.png";
import useAnimateProductToCard from "./useAnimateProductToCard";

const KioskProduct = ({
  product,
  parentId,
}: {
  product: Product;
  parentId: number;
}) => {
  const isAvailable =
    !product.status_enum ||
    product.status_enum === ProductStatusEnums.AVAILABLE;
  const t = useCommon();
  const animate = useAnimateProductToCard();
  const { id, categoryId } = useParams<{ id: string; categoryId: string }>();
  const history = useHistory();
  const addCartItem = useAddCartItem();

  const imageSrc =
    withCloudinaryBackgroundRemoval(product.image_cloudinary_id) || EmptyImage;

  const handleClick = () => {
    if (!isAvailable) return;
    try {
      if (isGreaterThanZero(product.sub_content)) {
        animate(product.item_id, imageSrc, false);

        return history.push(
          `/kiosk/menu/${id}/${categoryId}/${product.item_id}`
        );
      } else {
        animate(product.item_id, imageSrc);
        addCartItem({
          ...product,
          parent_id: parentId,
          quantity: 1,
        });
      }
    } catch (e) {
      alert(e);
    }
  };
  const TAP_THRESHOLD = 10; // px

  const startPos = useRef<{ x: number; y: number } | null>(null);
  const moved = useRef(false);

  const onPointerDown = (e: React.PointerEvent) => {
    startPos.current = { x: e.clientX, y: e.clientY };
    moved.current = false;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!startPos.current) return;

    const dx = Math.abs(e.clientX - startPos.current.x);
    const dy = Math.abs(e.clientY - startPos.current.y);

    if (dx > TAP_THRESHOLD || dy > TAP_THRESHOLD) {
      moved.current = true;
    }
  };


  const onPointerUp = () => {
    if (!moved.current) {
      handleClick();
    }

    startPos.current = null;
  };

  return (
    <motion.div
      role="button"
      tabIndex={-1}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      whileTap={{ scale: 0.95 }}
      animate={{ scale: 1 }}
      className="product-card"
      style={{ transition: "transform 300ms" }}
    >
      <div
        id={`product-${product.item_id}`}
        className="relative overflow-hidden p-[2rem]"
        style={{
          opacity: isAvailable ? 1 : 0.5,
          cursor: isAvailable ? "pointer" : "not-allowed",
        }}
      >
        <img
          src={imageSrc}
          decoding="async"
          onError={(e: any) => (e.target.src = EmptyImage)}
          className="mx-auto h-[30rem] w-full object-contain"
        />
        <div className="mt-[2rem] text-center text-[2.3rem] justify-between">
          <div style={{ fontWeight: "900" }}>{product?.name.toString()}</div>
          <div className="flex-[0_0_8rem]">{formatPrice(product?.price)}</div>
          {product.status_enum > ProductStatusEnums.AVAILABLE && (
            <div
              className="absolute left-[0] top-[30%] text-[5rem] text-[var(--error)] opacity-[0.5]"
              style={{ fontWeight: "100", transform: "rotate(-15deg)" }}
            >
              {t("soldOut")}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default KioskProduct;
